import React, { useEffect, useMemo } from "react";
import { alpha, useTheme } from "@mui/material/styles";
import {
    Box,
    Divider,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Typography,
    useMediaQuery,
} from "@mui/material";
import Container from "../../../components/Container";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../hooks/hooks";
import { User } from "../../../services/restserver-openapi";
import Main from "../../../layouts/Main";

import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
import CardMembershipRoundedIcon from "@mui/icons-material/CardMembershipRounded";
import HelpRoundedIcon from "@mui/icons-material/HelpRounded";

type PageItem = {
    id: string;
    href: string;
    title: string;
    subtitle: string;
    icon: React.ReactNode;
};

const allPages: PageItem[] = [
    {
        id: "personal",
        href: "/profil/personal",
        title: "Persönliche Daten",
        subtitle: "Kontakt- und Profildaten verwalten",
        icon: <PersonRoundedIcon />,
    },
    {
        id: "notifications",
        href: "/profil/notifications",
        title: "Benachrichtigungen",
        subtitle: "Infos und Hinweise steuern",
        icon: <NotificationsRoundedIcon />,
    },
    {
        id: "abos",
        href: "/profil/abos",
        title: "Meine Mitgliedschaft",
        subtitle: "Tarif und Abrechnung einsehen",
        icon: <CardMembershipRoundedIcon />,
    },
    {
        id: "support",
        href: "/profil/support",
        title: "Hilfe & Support",
        subtitle: "Antworten und Kontakt zum Team",
        icon: <HelpRoundedIcon />,
    },
];

interface Props {
    children: React.ReactNode;
}

const ProfileSettingsPage = ({ children }: Props): JSX.Element => {
    const theme = useTheme();
    const mode = theme.palette.mode;
    const isMd = useMediaQuery(theme.breakpoints.up("md"));

    const userRoleLogin = useAppSelector((state) => state.login.userRoleLogin);

    const navigate = useNavigate();
    const location = useLocation();

    const pages = useMemo(() => {
        if (userRoleLogin === User.userRole.AGENT) {
            return allPages.filter((p) => p.id !== "abos");
        }
        return allPages;
    }, [userRoleLogin]);

    useEffect(() => {
        if (!userRoleLogin) {
            // Fix: Routen heißen bei dir "/profil/..."
            navigate("/profil/personal");
        }
    }, [userRoleLogin, navigate]);

    const activePath = location.pathname;

    const ActionRow = (props: {
        icon: React.ReactNode;
        title: string;
        subtitle: string;
        active?: boolean;
        href: string;
    }) => {
        const { icon, title, subtitle, active, href } = props;

        return (
            <ListItem disablePadding>
                <ListItemButton
                    component={RouterLink}
                    to={href}
                    selected={active}
                    sx={{
                        borderRadius: 2,
                        px: 1.25,
                        py: 1,
                        gap: 0.5,
                        ...(active
                            ? {
                                bgcolor: "action.selected",
                                "&:hover": { bgcolor: "action.selected" },
                            }
                            : {}),
                    }}
                >
                    <ListItemIcon
                        sx={{
                            minWidth: 40,
                            color: active ? "primary.main" : "text.secondary",
                        }}
                    >
                        {icon}
                    </ListItemIcon>

                    <ListItemText
                        primary={
                            <Typography sx={{ fontWeight: 800 }} noWrap>
                                {title}
                            </Typography>
                        }
                        secondary={subtitle}
                        secondaryTypographyProps={{ color: "text.secondary" }}
                    />
                </ListItemButton>
            </ListItem>
        );
    };

    return (
        <Main>
            <Container>
                {/* Header im gleichen Stil wie BookingOverviewPage */}
                <Box data-aos={isMd ? "fade-right" : "fade-up"}>
                    <Typography
                        sx={{
                            textTransform: "uppercase",
                            fontWeight: 500,
                            letterSpacing: 1,
                        }}
                        gutterBottom
                        color="text.secondary"
                    >
                        Einstellungen
                    </Typography>

                    <Box mb={2}>
                        <Typography
                            variant="h3"
                            component="h2"
                            color="text.primary"
                            fontWeight={700}
                            lineHeight={1.2}
                        >
                            Verwalte{" "}
                            <Typography
                                component="span"
                                variant="inherit"
                                color="text.primary"
                                sx={{
                                    background: `linear-gradient(180deg, transparent 82%, ${alpha(
                                        mode === "light" ? theme.palette.secondary.main : theme.palette.primary.main,
                                        0.3
                                    )} 0%)`,
                                }}
                            >
                                Dein Profil
                            </Typography>
                        </Typography>

                        <Box mb={3}>
                            {userRoleLogin === User.userRole.HOST && (
                                <Typography variant="h6" component="p" color="text.secondary">
                                    Es freut uns sehr, Dich als Gastgeber bei Vanlife Travel begrüßen zu dürfen. Wir möchten Dir die
                                    Verwaltung Deiner Unterkünfte vereinfachen und Dir helfen, das Beste aus Deiner Gastgeberrolle
                                    herauszuholen. Solltest Du Fragen haben oder Unterstützung benötigen, steht unser Support Team jederzeit
                                    gerne zur Verfügung. Wir wünschen Dir viel Erfolg und freuen uns auf eine erfolgreiche Zusammenarbeit!
                                </Typography>
                            )}

                            {userRoleLogin === User.userRole.VANLIFER && (
                                <Typography variant="h6" component="p" color="text.secondary">
                                    Schön dass Du Teil der Open Travel Community bist. Wir möchten Dir die Verwaltung Deines Profils so
                                    einfach wie möglich machen. In diesem Bereich kannst Du Deine Profileinstellungen verwalten. Bitte
                                    achte darauf, dass Du korrekte Daten angibst, denn sonst können wir die Gastgeber nicht informieren, wer
                                    zu Ihnen kommt und Deine Reise kann nicht so stattfinden, wie Du es Dir gewünscht hast. Solltest Du
                                    Fragen oder Unterstützung benötigen steht dir unser Support Team gerne zur Verfügung. Wir wünschen Dir
                                    viel Spass.
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>

                {/* Layout im gleichen Stil wie BookingOverview (grid + Paper Cards) */}
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "320px 1fr" }, gap: 2 }}>
                    {/* LEFT: Navigation */}
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: 4,
                            p: 2,
                            height: "fit-content",
                            border: 0,
                            boxShadow: "none",
                            bgcolor: "background.paper",
                        }}
                    >
                        <List sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                            {pages.map((item, idx) => {
                                const isActive = activePath === item.href;

                                return (
                                    <React.Fragment key={item.id}>
                                        <ActionRow
                                            icon={item.icon}
                                            title={item.title}
                                            subtitle={item.subtitle}
                                            active={isActive}
                                            href={item.href}
                                        />

                                        {/* Optional: dezente Trennung wie bei ActionRow/Divider-Feeling */}
                                        {idx === pages.length - 2 && <Divider sx={{ my: 1.25 }} />}
                                    </React.Fragment>
                                );
                            })}
                        </List>
                    </Paper>

                    {/* RIGHT: Content */}
                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: 4,
                            p: 2,
                            border: 0,
                            boxShadow: "none",
                            bgcolor: "background.paper",
                            minHeight: 200,
                        }}
                    >
                        {children}
                    </Paper>
                </Box>
            </Container>
        </Main>
    );
};

export default ProfileSettingsPage;
